<template>
  <div class="page page-shell recharge-record">
    <van-nav-bar title="充值記錄" class="nav-bar">
      <template #left><van-icon name="arrow-left" color="#fff" @click="$router.push({ path: '/Mine' })" /></template>
    </van-nav-bar>
    <div class="content">
      <van-pull-refresh v-model="isLoading" :pulling-text="$t('reservation.refresh')" :loosing-text="$t('reservation.refreshpage')" :loading-text="$t('reservation.refresh')" @refresh="onRefresh">
        <div v-if="error" class="tips-card recharge-error" role="alert">
          <p>充值記錄載入失敗，請稍後重試。</p>
          <van-button round size="small" :loading="loading" @click="getUserRechargeList">重新載入</van-button>
        </div>
        <div v-if="loading && !list.length" class="recharge-loading"><van-loading size="24px">載入中…</van-loading></div>
        <van-empty v-else-if="!error && !list.length" description="暫無充值記錄" />
        <div v-if="list.length" class="record-list">
          <article class="record-card" v-for="(item, index) in list" :key="item.id || item.order_no || index">
            <div class="record-top">
              <span class="status status-tag" :class="statusClass(item.status)">{{ statusText(item) }}</span>
              <span class="money" :class="{ income: isCredited(item.status) }">{{ isCredited(item.status) ? '+' : '' }}{{ item.money }}</span>
            </div>
            <div v-if="item.order_no" class="meta-row"><span class="meta-label">訂單號</span><span class="meta-value">{{ item.order_no }}</span></div>
            <div class="meta-row"><span class="meta-label">提交時間</span><span class="meta-value">{{ item.create_time || '—' }}</span></div>
            <div class="meta-row"><span class="meta-label">入帳時間</span><span class="meta-value">{{ creditedTime(item) }}</span></div>
            <div v-if="hasDescription(item)" class="reject-card"><div class="reject-label">{{ isRejected(item.status) ? '失敗原因' : '備註' }}</div><div class="reject-value">{{ item.desc }}</div></div>
          </article>
        </div>
      </van-pull-refresh>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RechargeRecord',
  data() { return { list: [], isLoading: false, loading: false, error: false } },
  methods: {
    async getUserRechargeList() {
      if (this.loading) return false
      this.loading = true
      this.error = false
      try {
        const response = await this.$http({ method: 'get', url: 'user_get_recharge_list' })
        if (!response || response.code !== 200 || !Array.isArray(response.data)) throw new Error('Recharge records unavailable')
        this.list = response.data
        return true
      } catch (error) {
        this.error = true
        return false
      } finally { this.loading = false }
    },
    async onRefresh() {
      try { await this.getUserRechargeList() }
      finally { this.isLoading = false }
    },
    isCredited(status) { return [2, 4].includes(Number(status)) },
    isRejected(status) { return [3, 5].includes(Number(status)) },
    statusClass(status) { return this.isCredited(status) ? 'settled' : (this.isRejected(status) ? 'warning' : 'pending') },
    statusText(item) {
      if (this.isCredited(item.status)) return '充值成功'
      if (this.isRejected(item.status)) return '充值失敗'
      return item.status_text || (Number(item.status) === 0 ? '待付款' : '處理中')
    },
    creditedTime(item) {
      if (!this.isCredited(item.status)) return '—'
      return item.credited_time || item.update_time || '—'
    },
    hasDescription(item) { return !!String(item.desc || '').trim() },
  },
  created() {
    if (!localStorage.getItem('token')) this.$router.replace({ path: '/Login' })
    else this.getUserRechargeList()
  },
}
</script>

<style lang="less" scoped>
@import "../../assets/css/base.less";

.page-shell {
  min-height: 100%;
  background: #0b0b0d;
}

.content {
  padding: 16px;
  min-height: calc(100% - 46px);
}

.record-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.record-card {
  padding: 16px;
  border-radius: 18px;
  border: 1px solid #2a2a2a;
  background: #141419;
}

.record-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 22px;
  color: #fff;
  background: rgba(255, 255, 255, 0.1);
}

.status.success {
  color: #6fe3a1;
  background: rgba(37, 197, 94, 0.14);
}

.status.warning {
  color: #ffbe5c;
  background: rgba(255, 190, 92, 0.14);
}

.money {
  color: #ff6d7a;
  font-size: 34px;
  font-weight: 700;
}

.money.income {
  color: #6fe3a1;
}

.meta-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
}

.meta-label {
  color: #8f93a1;
  font-size: 22px;
}

.meta-value {
  color: #fff;
  font-size: 24px;
  text-align: right;
  word-break: break-word;
}

.reject-card {
  margin-top: 10px;
  padding: 12px;
  border-radius: 14px;
  background: rgba(255, 107, 107, 0.08);
  border: 1px solid rgba(255, 107, 107, 0.16);
}

.reject-label {
  color: #ff9da6;
  font-size: 22px;
  margin-bottom: 6px;
}

.reject-value {
  color: #fff;
  font-size: 24px;
  line-height: 1.6;
  word-break: break-word;
}

::v-deep .van-empty__description {
  color: #8f93a1;
}

.recharge-loading { display: flex; justify-content: center; padding: 3rem 1rem; }
.recharge-error { margin-bottom: 1rem; font-size: .85rem; line-height: 1.7; }
.recharge-error p { margin: 0 0 .75rem; }
</style>
